export type FilterOperator = "is" | "isNot";
export type FilterExclusions = Record<string, unknown>;

export function isActiveFilterCondition(value: unknown) {
  return value != null && value !== false
    && !(typeof value === "string" && value.trim().length === 0)
    && !(Array.isArray(value) && value.length === 0);
}

export function getFilterExclusions(filters: Record<string, unknown> | undefined): FilterExclusions {
  return (filters?.__exclude as FilterExclusions | undefined) ?? {};
}

export function setFilterCondition(filters: Record<string, unknown>, key: string, operator: FilterOperator, value: unknown): Record<string, unknown> {
  const next = { ...filters };
  const exclusions = { ...getFilterExclusions(filters) };
  const target = operator === "is" ? next : exclusions;
  const opposite = operator === "is" ? exclusions : next;
  if (!isActiveFilterCondition(value)) delete target[key];
  else target[key] = value;
  if (Array.isArray(value) && Array.isArray(opposite[key])) {
    const remaining = (opposite[key] as unknown[]).filter(item => !value.includes(item));
    if (remaining.length) opposite[key] = remaining;
    else delete opposite[key];
  } else if (value !== undefined && JSON.stringify(value) === JSON.stringify(opposite[key])) {
    delete opposite[key];
  }
  if ((key === "season" || key === "seasonScopeValues") && !isActiveFilterCondition(next[key])) next[key] = [];
  if (Object.keys(exclusions).length) next.__exclude = exclusions;
  else delete next.__exclude;
  return next;
}

export function moveFilterOperator(filters: Record<string, unknown>, key: string, from: FilterOperator, to: FilterOperator): Record<string, unknown> {
  if (from === to) return filters;
  const value = from === "is" ? filters[key] : getFilterExclusions(filters)[key];
  const destination = to === "is" ? filters[key] : getFilterExclusions(filters)[key];
  const merged = Array.isArray(value) && Array.isArray(destination)
    ? [...new Set([...destination, ...value])]
    : value;
  return setFilterCondition(setFilterCondition(filters, key, from, undefined), key, to, merged);
}

export async function buildNegativeFilterWhere<T extends { filters: Record<string, unknown> | undefined; search?: string }, W>(args: T, build: (args: T) => W | Promise<W>): Promise<W | { AND: unknown[] }> {
  const positiveFilters = { ...args.filters };
  delete positiveFilters.__exclude;
  const exclusions = Object.fromEntries(Object.entries(getFilterExclusions(args.filters)).filter(([, value]) => isActiveFilterCondition(value)));
  if (!Object.keys(exclusions).length) return await build(args);
  const positive = await build({ ...args, filters: positiveFilters });
  const negative = await Promise.all(
    Object.entries(exclusions).map(async ([key, value]) => ({
      // Reuse the owning list's exact positive semantics, including relation and derived filters.
      NOT: await build({ ...args, search: undefined, filters: { ...positiveFilters, [key]: value } }),
    })),
  );
  return { AND: [positive, ...negative] };
}
